import { z } from 'zod';

import {
  MODIFIER_TAP_CONSUMERS,
  isModifierCombo,
} from '../../shared/ipc.types';

export const AcknowledgeType = ['success', 'error'] as const;

// Re-export meeting-related payloads from shared
export { MeetingAudioEnergyLevelsPayload } from '../../shared/meetings.types';

export const zUInt64 = z.number().int().nonnegative();
export const PingPayload = z.object({
  timestamp: z.coerce.date(),
  text: z.string(),
});

export const AcknowledgePayload = z.object({
  type: z.enum(AcknowledgeType),
  timestamp: z.coerce.date(),
  info: z.string().nullable().optional(),
});

export const ObserverStatusPayload = z.object({
  isRunning: z.boolean(),
});

export const ExcludedWindowInfo = z.object({
  appName: z.string(),
  windowTitle: z.string(),
});

export const ExclusionUpdatePayload = z.object({
  appExcluded: z.boolean(),
  appName: z.string().nullable(),
  windowsExcluded: z.boolean(),
  excludedWindows: z.array(ExcludedWindowInfo),
});

export const SpriteUpdatePayload = z.object({
  success: z.boolean(),
  app_count: z.number(),
  sprite_url: z.string(),
  map_url: z.string(),
  heic_url: z.string().nullable().optional(),
  timestamp: zUInt64,
  message: z.string().nullable().optional(),
});

export const SystemEventPayload = z.object({
  event: z.string(),
  timestamp: zUInt64,
});

export const ApiSenderPayload = z.object({
  url: z.string(),
  httpMethod: z.enum(['GET', 'POST', 'PUT', 'DELETE']),
  contentType: z.enum([
    'application/json',
    'multipart/form-data',
    'application/x-www-form-urlencoded',
    'image/png',
  ]),
  requestBody: z.record(z.string(), z.unknown()),
  responseBody: z.record(z.string(), z.unknown()),
  errorMessage: z.string(),
  statusCode: z.number().int(),
  timestamp: zUInt64,
});

export const PermissionsStatusPayload = z.object({
  isGranted: z.boolean(),
});

export const PauseContextCollectionPayload = z.object({
  minutes: z.number(),
});

export const TraversedContentPayload = z.object({
  content: z.string(),
});

export const FocusedElementDataSchema = z.object({
  value: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  textSelection: z.string().nullable().optional(),
});

export const FrontAppContextPayload = z.object({
  pid: z.number().int().nonnegative(),
  appName: z.string(),
  bundleIdentifier: z.string().nullable().optional(),
  appIcon: z.string().nullable().optional(),
  windowId: z.number().int().positive().nullable().optional(),
  windowTitle: z.string().nullable().optional(),
  focusedElement: FocusedElementDataSchema.nullable().optional(),
});

const AppInfoSchema = z.object({
  appName: z.string(),
  appBundleId: z.string(),
  appPath: z.string().nullable().optional(),
});

export const ExcludeAppPayload = z.object({
  appToBeExcluded: AppInfoSchema,
});

export const ExcludeDomainPayload = z.object({
  domainToBeExcluded: z.string(),
});

export const IncludeAppPayload = z.object({
  appToBeIncluded: AppInfoSchema,
});

export const IncludeDomainPayload = z.object({
  domainToBeIncluded: z.string(),
});

export const ModifierTapDetectedPayload = z.object({
  consumer: z.enum(MODIFIER_TAP_CONSUMERS),
  modifier: z.string().refine(isModifierCombo, 'not a modifier combo'),
  tapCount: z.number().int().min(1).max(5),
});

export const ModifierHoldEventPayload = z.object({
  consumer: z.enum(MODIFIER_TAP_CONSUMERS),
  modifier: z.string().refine(isModifierCombo, 'not a modifier combo'),
});

export const AppWindowInfoSchema = z
  .object({
    pid: z.number().int(),
    windowId: z.number().int(),
    name: z.string(),
    windowTitle: z.string(),
    bundleIdentifier: z.string().nullable().optional(),
    iconURL: z.string().nullable().optional(),
  })
  .strict();

export const MentionableWindowsPayload = z
  .object({
    windows: z.array(AppWindowInfoSchema),
  })
  .strict();

export const CapturableWindowsPayload = z
  .object({
    windows: z.array(AppWindowInfoSchema),
  })
  .strict();

export const AppScreenshotCapturedPayload = z.object({
  windowId: z.number().int(),
  imageData: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

export type AppWindowInfo = z.infer<typeof AppWindowInfoSchema>;

export const GetSnapshotDumpAppsPayload = z.object({}).strict();

export const GetSnapshotDumpArchivesPayload = z.object({
  appNames: z.array(z.string()),
});

export const SnapshotDumpAppsPayload = z.object({
  apps: z.array(z.string()),
});

export const SnapshotDumpArchivesPayload = z.object({
  archives: z.array(
    z.object({
      appName: z.string(),
      filePath: z.string(),
      fileName: z.string(),
    })
  ),
});
